import React, { useState } from 'react';
import { authApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

// Email-OTP two-factor. There is no QR/enrolment step: codes go to the account's
// own address. Enabling issues a set of one-time recovery codes, shown once.
export default function TwoFactorSetup() {
  const { user, fetchUser } = useAuth();
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [emailHint, setEmailHint] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const enabled = Boolean(user?.is_2fa_enabled);

  const handleEnable = async () => {
    setBusy(true);
    try {
      const { data } = await authApi.enable2FA();
      setRecoveryCodes(data.recoveryCodes || []);
      setEmailHint(data.emailHint || '');
      await fetchUser();
      toast.success('Two-factor is on');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not enable two-factor');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await authApi.disable2FA({ password });
      setPassword('');
      await fetchUser();
      toast.success('Two-factor is off');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not disable two-factor');
    } finally {
      setBusy(false);
    }
  };

  if (recoveryCodes) {
    return (
      <div className="bg-muted/50 rounded-md border border-border p-6">
        <h3 className="text-sm font-semibold tracking-tight text-foreground">Save your recovery codes</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Each code can be used once instead of the emailed code. They will not be shown again.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2 font-mono text-sm bg-background border border-border rounded-md p-4">
          {recoveryCodes.map((code) => (
            <span key={code} className="text-center py-1">{code}</span>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setRecoveryCodes(null)}
          className="mt-4 inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2"
        >
          I have saved these codes
        </button>
      </div>
    );
  }

  if (enabled) {
    return (
      <div className="bg-muted/50 rounded-md border border-border p-6">
        <h3 className="text-sm font-semibold tracking-tight text-foreground">Two-factor authentication is on</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Each sign-in sends a 6-digit code to your account email. Your recovery codes work in its place.
        </p>
        <form onSubmit={handleDisable} className="mt-4 space-y-3 max-w-sm">
          <label className="text-sm font-medium text-foreground block">
            Enter your password to turn it off
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-border bg-background hover:bg-muted h-9 px-4 py-2 disabled:opacity-50"
          >
            {busy ? 'Turning off…' : 'Disable two-factor'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-muted/50 rounded-md border border-border p-6 flex flex-col items-start gap-4">
      <div>
        <h3 className="text-sm font-semibold tracking-tight text-foreground">Set up two-factor authentication</h3>
        <p className="text-sm text-muted-foreground mt-1">
          When you sign in, we email a 6-digit code{emailHint ? ` to ${emailHint}` : ' to your account address'}.
          You will also get one-time recovery codes to save.
        </p>
      </div>
      <button
        onClick={handleEnable}
        disabled={busy || !user?.email}
        className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2 disabled:opacity-50"
      >
        {busy ? 'Enabling…' : 'Enable two-factor'}
      </button>
      {!user?.email && (
        <p className="text-xs text-red-600">Add an email address to your account first.</p>
      )}
    </div>
  );
}
